import React, { useEffect, useRef, useCallback, memo } from 'react';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { scrollMainToTop } from '../../hooks/useScrollBehavior';

/**
 * A "Back to Top" floating button that appears on mobile when the user
 * scrolls down past a threshold. Smoothly scrolls the page back to the top.
 *
 * Uses direct DOM style manipulation instead of React state so scroll
 * events never trigger a React re-render.
 */
const BackToTopButton = memo(function BackToTopButton() {
  const isMobile = useIsMobile();
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const wasVisible = useRef(false);

  const handleScroll = useCallback(() => {
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const main = document.querySelector('main');
      const scrollY = main && main.scrollTop > 0 ? main.scrollTop : window.scrollY;
      const shouldShow = scrollY > 400;
      if (shouldShow === wasVisible.current) return;
      wasVisible.current = shouldShow;
      const el = btnRef.current;
      if (!el) return;
      el.style.opacity = shouldShow ? '1' : '0';
      el.style.transform = shouldShow ? 'translateY(0)' : 'translateY(20px)';
      el.style.pointerEvents = shouldShow ? 'auto' : 'none';
    });
  }, []);

  useEffect(() => {
    if (!isMobile) return;

    window.addEventListener('scroll', handleScroll, { passive: true });
    const main = document.querySelector('main');
    if (main) main.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      const mainEl = document.querySelector('main');
      if (mainEl) mainEl.removeEventListener('scroll', handleScroll);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [isMobile, handleScroll]);

  const scrollToTop = () => {
    scrollMainToTop();
  };

  if (!isMobile) return null;

  return (
    <button
      ref={btnRef}
      onClick={scrollToTop}
      className="fixed bottom-16 left-4 z-[54] w-9 h-9 rounded-full
                 bg-white dark:bg-gray-700 shadow-lg border border-gray-200
                 dark:border-gray-600 flex items-center justify-center
                 text-gray-600 dark:text-gray-300
                 hover:bg-gray-50 dark:hover:bg-gray-600
                 active:scale-95 transition-all duration-200"
      aria-label="Back to top"
      title="Back to top"
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        opacity: 0,
        transform: 'translateY(20px)',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        pointerEvents: 'none',
      }}
    >
      <KeyboardArrowUpIcon />
    </button>
  );
});

export default BackToTopButton;
